import React, { useContext, useState, useEffect } from "react";
import { ReactElement } from "react";
import { UserContext } from "../contexts/UserContext";
import { Grid, Typography, CircularProgress, Box, IconButton, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import LinkIcon from '@mui/icons-material/Link';
import { Action, Category } from "../analytics/Tracking";
import { useTracking } from "../analytics/useTracking";
import { useEnvironment } from "../hooks/useEnvironment";
import { usePricing } from "../hooks/usePricing";
import { usePaymentLink } from "../hooks/usePaymentLink";
import { useAlert } from "../contexts/alert/useAlert";
import { useConfig } from "../hooks/useConfig";
import { BillingToggle } from "./subscription/BillingToggle";
import { PricingCard } from "./subscription/PricingCard";
import { PricingBadges } from "./subscription/PricingBadges";
import { FeaturesList } from "./subscription/FeaturesList";
import { PriceDisplay } from "./subscription/PriceDisplay";
import { PaymentLinkCard } from "./subscription/PaymentLinkCard";
import { SubscribeButton } from "./subscription/SubscribeButton";
import { SubscriptionTerms } from "./subscription/SubscriptionTerms";

type BillingPeriod = 'monthly' | 'annual';

interface SubscribeProps {
    onCompleted?: () => void;
  }

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function Subscribe(_props: SubscribeProps): ReactElement {
  const { user, authToken } = useContext(UserContext);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('annual');
  const [showPaymentLink, setShowPaymentLink] = useState(false);
  const [isDevelopment] = useEnvironment();
  const { track } = useTracking();
  const alert = useAlert();
  const theme = useTheme();
  const isBreakpointGreaterThanXs = useMediaQuery(theme.breakpoints.up('sm'));

  const config = useConfig();
  const { pricingInfo, isLoading: pricingLoading, pricingError } = usePricing(authToken || null);
  const paymentLink = usePaymentLink(user?.idPId, user?.email, billingPeriod, pricingInfo);

  useEffect(() => {
    if (pricingError) {
      alert.showMessage(pricingError, 'error');
      if (_props.onCompleted) {
        _props.onCompleted();
      }
    }
  }, [pricingError, alert, _props]);

  const handleBillingPeriodChange = (
    _event: React.MouseEvent<HTMLElement>,
    newPeriod: BillingPeriod | null,
  ) => {
    if (newPeriod !== null) {
      setBillingPeriod(newPeriod);
      }
  };

  const handleUpgradeClick = () => {
    track.event(Category.User, Action.PurchaseStarted, user?.idPId);
  };

  if (!user || user?.plan?.subscription) {
    return <></>;
  }

  if (pricingLoading) {
    return (
      <Grid container sx={{ padding: 3 }} spacing={3} direction="column" justifyContent="center" alignItems="center">
        <Grid>
          <CircularProgress />
        </Grid>
        <Grid>
          <Typography variant="body2" color="text.secondary">
            Loading pricing information...
          </Typography>
        </Grid>
      </Grid>
    );
  }

  if (!pricingInfo) {
    return <></>;
  }

  return (
    <Grid container sx={{ padding: 3, marginBottom: 3 }} spacing={3} direction="column" justifyContent="space-between" alignItems="center">
      <Grid sx={{ mb: 2 }}>
        <BillingToggle value={billingPeriod} onChange={handleBillingPeriodChange} />
      </Grid>

      <Grid sx={{ width: '100%' }}>
        <PricingCard sx={{ maxWidth: '100%' }}>
          <PricingBadges isAnnual={billingPeriod === 'annual'} isDevelopment={isDevelopment()} />
          
          <Typography variant="h4" component="h2" sx={{ mb: 1, fontWeight: 600 }}>
            {pricingInfo.product.name}
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minHeight: 48 }}>
            {pricingInfo.product.description}
          </Typography>

          {isBreakpointGreaterThanXs && (
            <FeaturesList features={pricingInfo.product.features || []} />
          )}
          <PriceDisplay pricingInfo={pricingInfo} billingPeriod={billingPeriod} />
          <SubscribeButton 
            pricingInfo={pricingInfo}
            billingPeriod={billingPeriod}
            authToken={authToken}
            onUpgradeClick={handleUpgradeClick}
          />
        </PricingCard>
        </Grid>

      <Grid sx={{ width: '100%', mt: 1 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: 2,
          pt: 1,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Tooltip title={showPaymentLink ? "Hide payment link" : "Show payment link to share"} arrow>
            <IconButton
              size="small"
              onClick={() => setShowPaymentLink(!showPaymentLink)}
              disabled={!paymentLink}
              sx={{ color: 'text.secondary' }}
              aria-label="Toggle payment link"
            >
              <LinkIcon />
            </IconButton>
            </Tooltip>
        </Box>
      </Grid>

      {showPaymentLink && paymentLink && (
        <Grid sx={{ width: '100%', mt: 2 }}>
          <PaymentLinkCard
            paymentLink={paymentLink}
            userId={user.idPId}
            monthlyPrice={pricingInfo.prices.monthly?.amount}
            discountPercent={config.INTRODUCTORY_DISCOUNT_PERCENT}
            discountMonths={config.INTRODUCTORY_DISCOUNT_MONTHS}
            onCopy={() => setShowPaymentLink(false)}
          />
        </Grid>
      )}

        <Grid>
        <SubscriptionTerms />
      </Grid>
    </Grid>
  );
}
